/**
 * @file BusinessUnitMultiSelect.js
 * @description A multi-select dropdown component for selecting business units.
 */

import React, { useState, useEffect } from "react";
import { SearchableDropdown } from "../../helpers/SearchableDropdown_style_b_v2";
import { fetchBusinessUnits } from "../../../services/orgLevelService";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * Represents a multi-select component for selecting business units.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {Array} props.selectedBusinessUnits - The array of selected business units.
 * @param {Function} props.onBusinessUnitsChange - The function to handle business unit selection change.
 * @param {string} [props.label="Business Units"] - The label for the component.
 * @param {boolean} [props.isRequired=false] - Indicates if the component is required.
 * @param {boolean} props.resetTrigger - The trigger to reset the component.
 * @param {string} props.tenantId - The ID of the tenant.
 * @param {boolean} [props.showSelectedItemsInField=true] - Indicates if the selected items should be shown in the field.
 * @param {boolean} [props.showSelectionBox=true] - Indicates if the selection box should be shown.
 * @returns {JSX.Element} The rendered BusinessUnitMultiSelect component.
 */
const BusinessUnitMultiSelect = ({
  className,
  selectedBusinessUnits,
  onBusinessUnitsChange,
  label = "Business Units",
  isRequired = false,
  resetTrigger,
  tenantId,
  showSelectedItemsInField = true,
  showSelectionBox = true,
  showSelectedCount = false,
}) => {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBusinessUnits = async () => {
      try {
        setIsLoading(true);
        const data = await fetchBusinessUnits(tenantId);
        setBusinessUnits(data);
      } catch (error) {
        console.error("Could not fetch business units:", error);
        Toast.error(getText('msg_ac76c6ffea9e'));
      } finally {
        setIsLoading(false);
      }
    };

    loadBusinessUnits();
  }, [tenantId]);

  const handleBusinessUnitsChange = (selectedOptions) => {
    onBusinessUnitsChange(selectedOptions);
  };

  return (
    <div className={`form-group mb-3 ${className}`}>
      <label htmlFor="business-unit-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <SearchableDropdown
        id="business-unit-dropdown"
        options={businessUnits}
        optionIDField="id"
        updateOptions={handleBusinessUnitsChange}
        savedOptions={selectedBusinessUnits}
        savedOptionIDField="id"
        disabled={businessUnits.length === 0}
        isLoading={isLoading}
        resetTrigger={resetTrigger}
        showSelectedItemsInField={showSelectedItemsInField}
        showSelectionBox={showSelectionBox}
        showSelectedCount={showSelectedCount}
        allSelectedLabel={getText('msg_d5734818c14c')}
      />
    </div>
  );
};

export default BusinessUnitMultiSelect;
